import {defineCustomElement} from 'extensions/ui-utils';
import stylesheet from './x-drawer.scss?as=stylesheet';
import {html, LitElement} from 'lit';
import {classMap} from 'lit/directives/class-map.js';

const SLOT_NAMES = ['trigger'];

export class Drawer extends LitElement {
	// #region initialization

	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			direction: {
				type: String
			},
			open: {
				reflect: true,
				type: Boolean
			}
		};
	}

	// #endregion initialization

	constructor () {
		super();

		/**
		 * @type {'bottom'|'top'}
		 */
		this.direction = 'bottom';

		/**
		 * @type {boolean}
		 */
		this.open = false;

		// Bind handlers
		this.handleDocumentKeyup = this._handleDocumentKeyup.bind(this);
	}

	// #region public api methods

	openDrawer () {
		this.open = true;
	}

	closeDrawer () {
		this.open = false;
	}

	toggleDrawer () {
		this.open = !this.open;
	}

	// #endregion public api methods

	// #region event handlers

	/**
	 * @param {KeyboardEvent} e
	 */
	_handleDocumentKeyup (e) {
		switch (e.key) {
			case 'Escape': {
				this.closeDrawer();
				break;
			}
			default:
				break;
		}
	}

	/**
	 * @param {Event} e
	 */
	handleTriggerClick (e) {
		// Prevent toggling if target contains a matching slot attribute
		if (!e.composedPath().some(element => SLOT_NAMES.includes(element.slot))) {
			this.toggleDrawer();
		}
	}

	// #endregion event handlers

	// #region lifecycle

	updated (changedProperties) {
		if (changedProperties.has('open')) {
			// Add event listener and disable body scrolling
			if (this.open) {
				document.addEventListener('keyup', this.handleDocumentKeyup);

				document.body.classList.add('u-scroll-lock');
			}
			else {
				document.removeEventListener('keyup', this.handleDocumentKeyup);

				document.body.classList.remove('u-scroll-lock');
			}

			// Dispatch change event
			this.dispatchEvent(new CustomEvent('change', {
				bubbles: true
			}));
		}
	}

	// #endregion lifecycle

	// #region render methods

	render () {
		const containerClasses = {
			container: true,
			[`direction--${this.direction}`]: true,
			'is--open': this.open
		};

		return html`
			<div class="${classMap(containerClasses)}">
				<div class="container-inner">
					<div class="drawer">
						<div class="drawer-container">
							<div class="drawer-wrap">
								<header class="drawer-header">
									<div class="heading">
										<slot name="heading"></slot>
									</div>
									<button class="rounded-button close-button" aria-label="Close Details" @click="${this.closeDrawer}"></button>
								</header>
								<div class="drawer-content">
									<slot></slot>
								</div>
							</div>
						</div>
					</div>
					<button class="trigger" @click="${this.handleTriggerClick}">
						<div class="trigger-wrap">
							<div class="trigger-left">
								<div class="rounded-button toggle-button" aria-label="Toggle Details"></div>
								<slot name="trigger-title"></slot>
							</div>
							<div class="trigger-right">
								<slot name="trigger"></slot>
							</div>
						</div>
					</button>
				</div>
			</div>
		`;
	}

	// #endregion render methods
};

defineCustomElement('x-drawer', Drawer);
